import { Component } from '@theme/component';

/*
 * <echt-rewards-hero> — the tier-card stack in sections/echt-rewards-hero.liquid.
 *
 * Progressive enhancement only: the section renders Member active with Bronze
 * peeking without this module. This component owns one active index (0–4) and
 * writes it to attributes; the CSS draws everything from those attributes and
 * the 3D runtime (assets/echt-rewards-3d.js) watches [data-active] on the slide.
 *
 * Every gesture moves exactly one tier: buttons, ArrowLeft/ArrowRight on the
 * rail, a horizontal pointer swipe over 35px, and horizontal wheel/trackpad
 * travel accumulated past a threshold. The group never pans.
 *
 * It also measures the continuous rewards band: every [data-echt-rewards-band]
 * section on the page (hero, tiers, boxes, included) gets
 * --rewards-band-offset (its top minus the first one's) and
 * --rewards-band-height (first top to last bottom), so their backgrounds paint
 * one texture sheet with no step at the section boundaries.
 *
 * The pure helpers are exported for tests/unit/rewards-hero.test.mjs.
 */

export const SWIPE_THRESHOLD = 35;
export const WHEEL_MAX_STEP = 60;
export const WHEEL_MIN_DELTA = 3;
export const WHEEL_IDLE_MS = 180;

/** @param {number} index @param {number} count */
export function clampIndex(index, count) {
  return Math.max(0, Math.min(count - 1, index));
}

/**
 * Where a slide sits relative to the active one. Only the active card and the
 * one after it are shown, except at the end, where the previous card is.
 * @param {number} slide @param {number} active @param {number} count
 * @returns {'active' | 'next' | 'prev' | 'before' | 'after'}
 */
export function slidePosition(slide, active, count) {
  if (slide === active) return 'active';
  if (slide === active + 1) return 'next';
  if (active === count - 1 && slide === active - 1) return 'prev';
  return slide < active ? 'before' : 'after';
}

/**
 * The tier step for a finished pointer gesture: dragging left goes forward.
 * @param {number} dx @param {number} dy @returns {-1 | 0 | 1}
 */
export function swipeStep(dx, dy, threshold = SWIPE_THRESHOLD) {
  if (Math.abs(dx) <= threshold || Math.abs(dx) <= Math.abs(dy)) return 0;
  return dx < 0 ? 1 : -1;
}

/** Wheel travel needed for one tier: 42% of the rail, kept within 260–420px. */
export function wheelThreshold(railWidth) {
  return Math.max(260, Math.min(420, railWidth * 0.42));
}

/** deltaX in pixels, whatever the wheel's deltaMode (line = 16px, page = rail width). */
export function wheelPixels(deltaX, deltaMode, railWidth) {
  if (deltaMode === 1) return deltaX * 16;
  if (deltaMode === 2) return deltaX * railWidth;
  return deltaX;
}

/**
 * Accumulates horizontal wheel travel. `push` returns the signed number of
 * tiers the travel crossed (in practice 0 or ±1: each event adds at most 60).
 * The total resets after 180ms idle or on a change of direction; deltas under
 * 3px are ignored as the inertial tail.
 */
export function createWheelAccumulator() {
  let total = 0;
  let direction = 0;
  let last = -Infinity;

  return {
    /** @param {number} delta @param {number} now @param {number} threshold */
    push(delta, now, threshold) {
      const sign = Math.sign(delta);
      if (sign !== direction || now - last > WHEEL_IDLE_MS) total = 0;
      direction = sign;
      last = now;
      if (Math.abs(delta) < WHEEL_MIN_DELTA) return 0;
      total += sign * Math.min(Math.abs(delta), WHEEL_MAX_STEP);
      let steps = 0;

      while (Math.abs(total) >= threshold) {
        steps += sign;
        total -= sign * threshold;
      }

      return steps;
    },
    reset() {
      total = 0;
    },
    get total() {
      return total;
    },
  };
}

/** "Member · 1 of 5" */
export function statusText(name, index, count) {
  return `${name} · ${index + 1} of ${count}`;
}

/**
 * The continuous rewards band. Given the [data-echt-rewards-band] sections'
 * rects in document order, returns each one's offset from the first one's top
 * and the band's total height (first top to last bottom), in px rounded to
 * 0.01. Differences of rect tops are scroll-independent, which matters because
 * Horizon scrolls .page-wrapper, not the window, above 990px.
 * @param {{ top: number, bottom: number }[]} rects
 * @returns {{ offsets: number[], height: number }}
 */
export function bandLayout(rects) {
  if (!rects.length) return { offsets: [], height: 0 };
  const round = (n) => Math.round(n * 100) / 100;
  const top0 = rects[0].top;
  return {
    offsets: rects.map((rect) => round(rect.top - top0)),
    height: round(rects[rects.length - 1].bottom - top0),
  };
}

/** Sets a custom property only when its value changes (no style churn). */
function setProperty(element, name, value) {
  if (element.style.getPropertyValue(name) !== value) element.style.setProperty(name, value);
}

const RETRY_DELAYS = [2000, 4000];

const pointsText = (count) => `${count.toLocaleString('en-AU')} ${count === 1 ? 'point' : 'points'}`;

/**
 * The bar under the card, or null when Smile's numbers cannot draw one. Caps at
 * 99 while points are owed, because rounding can otherwise fill it early.
 * `short` is the visible wording and `spoken` the bar's aria-valuetext.
 */
export function standingBar(progress) {
  if (progress?.next === null) return { percent: 100, short: 'Top tier', spoken: 'Top tier' };
  if (!(progress?.remaining > 0)) return null;
  const { remaining, next } = progress;
  const percent = Math.min(progress.percent, 99);
  const left = remaining === 1 ? pointsText(1) : remaining.toLocaleString('en-AU');
  return { percent, short: `${left} to ${next}`, spoken: `${percent}%, ${pointsText(remaining)} to ${next}` };
}

/** "35 points · 265 to Bronze", or just the points when there is no usable progress. */
export function standingText(balance, bar) {
  return bar ? `${pointsText(balance)} · ${bar.short}` : pointsText(balance);
}

/**
 * Hands over the member's status through the reader the header shares, so a page
 * load makes one status read and one wallet read. The reader is imported only
 * here, for a signed-in customer, so a guest never requests it and a failed
 * import leaves the carousel working. A rejected read or import is retried about
 * 2s then 4s later (Smile answers bursts with HTTP 429), and initialization, which
 * can arrive late, starts over with fresh retries. The 30-second window and
 * disposer invalidate every pending SDK response. An unknown tier or invalid
 * balance reads as no member, so neither selects a tier.
 * No SDK initialization, credentials, customer identity retention or writes.
 */
export function watchMembershipTier({ getSmile, events, tierNames, select, report, timeoutMs = 30000 }) {
  let stopped = false;
  let generation = 0;
  let retries = 0;
  let retryTimer;
  const stop = () => {
    stopped = true;
    ++generation;
    clearTimeout(timer);
    clearTimeout(retryTimer);
    events.removeEventListener('smile-js-initialized', initialized);
  };
  const attempt = async () => {
    if (stopped) return;
    clearTimeout(retryTimer);
    const request = ++generation;
    try {
      const { readAccountRewards } = await import('@theme/echt-account-rewards');
      if (stopped || request !== generation) return;
      const rewards = await readAccountRewards(getSmile());
      if (stopped || request !== generation || !rewards) return;
      stop();
      const index = tierNames.findIndex((tier) => tier.trim().toLowerCase() === rewards.tier.toLowerCase());
      if (index >= 0) select(index);
      report(rewards);
    } catch {
      // Includes the synchronous current() method throwing before SDK initialization.
      if (stopped || request !== generation) return;
      const delay = RETRY_DELAYS[retries];
      if (delay) retryTimer = setTimeout(() => { retries += 1; void attempt(); }, delay);
    }
  };
  const initialized = () => { retries = 0; void attempt(); };
  const timer = setTimeout(stop, timeoutMs);
  events.addEventListener('smile-js-initialized', initialized);
  void attempt();
  return stop;
}

/**
 * @typedef {object} RewardsHeroRefs
 * @property {HTMLButtonElement} previous
 * @property {HTMLButtonElement} next
 * @property {HTMLElement} rail
 * @property {HTMLElement[]} slides
 * @property {HTMLElement} status
 */

/** @extends {Component<RewardsHeroRefs>} */
export class EchtRewardsHero extends Component {
  requiredRefs = ['previous', 'next', 'rail', 'slides', 'status'];

  /** @type {AbortController | undefined} */
  #controller;
  #active = 0;
  #wheel = createWheelAccumulator();
  /** @type {{ x: number, y: number } | null} */
  #gesture = null;
  /** @type {ResizeObserver | undefined} */
  #bandObserver;
  /** @type {Element[]} */
  #bandElements = [];
  #bandFrame = 0;
  #stopMembership;
  #manuallyNavigated = false;
  /** @type {HTMLElement | null | undefined} */
  #standing;

  connectedCallback() {
    super.connectedCallback();
    this.#controller?.abort();
    this.#stopMembership?.();
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const { previous, next, rail } = this.refs;

    this.#standing = this.closest('.echt-rewards-hero')?.querySelector('[data-rewards-standing]');
    this.#clearStanding();
    this.#active = Math.max(0, this.refs.slides.findIndex((slide) => slide.hasAttribute('data-active')));

    previous.addEventListener('click', () => this.step(-1), { signal });
    next.addEventListener('click', () => this.step(1), { signal });

    rail.addEventListener('keydown', (event) => {
      if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
      event.preventDefault();
      this.step(event.key === 'ArrowRight' ? 1 : -1);
    }, { signal });

    rail.addEventListener('pointerdown', (event) => {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      this.#gesture = { x: event.clientX, y: event.clientY };
      rail.setPointerCapture?.(event.pointerId);
      rail.setAttribute('data-dragging', '');
    }, { signal });

    rail.addEventListener('pointerup', (event) => {
      if (!this.#gesture) return;
      const step = swipeStep(event.clientX - this.#gesture.x, event.clientY - this.#gesture.y);
      this.#endGesture();
      if (step) this.step(step);
    }, { signal });

    rail.addEventListener('pointercancel', () => this.#endGesture(), { signal });

    rail.addEventListener('wheel', (event) => {
      if (event.ctrlKey || Math.abs(event.deltaX) <= Math.abs(event.deltaY)) return;
      event.preventDefault();
      this.#claimNavigation();
      const width = rail.clientWidth;
      const pixels = wheelPixels(event.deltaX, event.deltaMode, width);
      const steps = this.#wheel.push(pixels, performance.now(), wheelThreshold(width));

      for (let i = 0; i < Math.abs(steps); i += 1) {
        if (!this.step(Math.sign(steps))) {
          this.#wheel.reset();
          break;
        }
      }
    }, { signal, passive: false });

    this.#render();
    this.#watchBand(signal);
    // The server renders the standing block for signed-in customers only, so a guest reads and retries nothing.
    if (this.#standing) {
      this.#stopMembership = watchMembershipTier({
        getSmile: () => window.Smile,
        events: document,
        tierNames: this.refs.slides.map((slide) => slide.dataset.tierName ?? ''),
        select: (index) => { if (this.isConnected && !this.#manuallyNavigated) this.show(index); },
        report: (rewards) => { if (this.isConnected) this.#showStanding(rewards); },
      });
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#stopMembership?.();
    this.#clearStanding();
    this.#endGesture();
    this.#wheel.reset();
    this.#controller?.abort();
    this.#bandObserver?.disconnect();
    this.#bandObserver = undefined;
    this.#bandElements = [];
    cancelAnimationFrame(this.#bandFrame);
    this.#bandFrame = 0;
  }

  /**
   * Publishes --rewards-band-offset / --rewards-band-height on every
   * [data-echt-rewards-band] section so the four dark sections paint one
   * continuous texture sheet. Re-measured on window resize, on any band
   * section resizing, and when the editor loads or unloads a section (the band
   * list is re-queried on every measure).
   * @param {AbortSignal} signal
   */
  #watchBand(signal) {
    const schedule = () => this.#scheduleBand();
    this.#bandObserver?.disconnect();
    this.#bandObserver = typeof ResizeObserver === 'function' ? new ResizeObserver(schedule) : undefined;
    this.#bandElements = [];
    window.addEventListener('resize', schedule, { signal });
    document.addEventListener('shopify:section:load', schedule, { signal });
    document.addEventListener('shopify:section:unload', schedule, { signal });
    schedule();
  }

  #scheduleBand() {
    if (this.#bandFrame) return;
    this.#bandFrame = requestAnimationFrame(() => {
      this.#bandFrame = 0;
      this.#publishBand();
    });
  }

  #publishBand() {
    const bands = [...document.querySelectorAll('[data-echt-rewards-band]')];
    const changed = bands.length !== this.#bandElements.length || bands.some((band, i) => band !== this.#bandElements[i]);

    if (changed && this.#bandObserver) {
      this.#bandObserver.disconnect();
      for (const band of bands) this.#bandObserver.observe(band);
    }

    this.#bandElements = bands;
    // All reads first, then all writes, in this one frame. Writing the custom
    // properties only repaints the background, so the observer is not re-fired
    // by them; setProperty skips unchanged values.
    const { offsets, height } = bandLayout(bands.map((band) => band.getBoundingClientRect()));
    const bandHeight = `${height}px`;

    for (const [i, band] of bands.entries()) {
      if (!(band instanceof HTMLElement)) continue;
      setProperty(band, '--rewards-band-offset', `${offsets[i]}px`);
      setProperty(band, '--rewards-band-height', bandHeight);
    }
  }

  /** The active tier index (0 = Member). */
  get active() {
    return this.#active;
  }

  /**
   * Moves one tier forward (1) or back (-1).
   * @param {number} direction
   * @returns {boolean} whether the active tier changed
   */
  step(direction) {
    this.#claimNavigation();
    return this.show(this.#active + direction);
  }

  /**
   * @param {number} index
   * @returns {boolean} whether the active tier changed
   */
  show(index) {
    const target = clampIndex(index, this.refs.slides.length);
    if (target === this.#active) return false;
    this.#active = target;
    this.#render();
    return true;
  }

  /**
   * The member's own status: written once and never touched by #render, so it
   * stays put, in their colour, while they browse the other cards.
   */
  #showStanding({ tier, balance, progress }) {
    const standing = this.#standing;
    if (!standing) return;
    const bar = standingBar(progress);
    const track = standing.querySelector('[data-rewards-standing-bar]');
    track.hidden = !bar;
    if (bar) {
      track.setAttribute('aria-valuenow', String(bar.percent));
      track.setAttribute('aria-valuetext', bar.spoken);
      standing.querySelector('[data-rewards-standing-fill]').style.setProperty('--standing-progress', `${bar.percent}%`);
    }
    standing.querySelector('[data-rewards-standing-text]').textContent = standingText(balance, bar);
    standing.dataset.tier = tier.toLowerCase();
    standing.hidden = false;
  }

  #clearStanding() {
    if (!this.#standing) return;
    this.#standing.hidden = true;
    this.#standing.querySelector('[data-rewards-standing-text]').textContent = '';
  }

  #claimNavigation() {
    this.#manuallyNavigated = true;
  }

  #endGesture() {
    this.#gesture = null;
    this.refs.rail.removeAttribute('data-dragging');
  }

  #render() {
    const { slides, previous, next, rail, status } = this.refs;
    const count = slides.length;
    const active = this.#active;

    for (const [i, slide] of slides.entries()) {
      slide.setAttribute('data-position', slidePosition(i, active, count));
      slide.toggleAttribute('data-active', i === active);
      if (i === active) slide.removeAttribute('aria-hidden');
      else slide.setAttribute('aria-hidden', 'true');
    }

    const current = slides[active];
    if (current?.dataset.tier) this.setAttribute('data-active-tier', current.dataset.tier);
    rail.toggleAttribute('data-at-end', active === count - 1);

    // A button that disables itself while focused would drop focus to <body>;
    // hand it to the other arrow, which can always move back.
    const focused = document.activeElement;
    previous.disabled = active === 0;
    next.disabled = active === count - 1;
    if (focused === previous && previous.disabled) next.focus();
    if (focused === next && next.disabled) previous.focus();

    const text = statusText(current?.dataset.tierName ?? '', active, count);
    if (status.textContent !== text) status.textContent = text;
  }
}

if (!customElements.get('echt-rewards-hero')) customElements.define('echt-rewards-hero', EchtRewardsHero);
